import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Container, Typography, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Add } from '@material-ui/icons';
import { useSnackbar } from 'notistack';

import InviteUserModal from './InviteUserModal';
import ImportUsersModal from './ImportUsersModal';
import ResendInvitationModal from './ResendInvitationModal';
import UpdateUserModal from './UpdateUserModal';
import UserStatusModal from './UserStatusModal';
import UsersTable from '../../components/users/UsersTable';
import { ROLES } from '../../constants/general';
import { ACTIONS } from '../../constants/logs';
import { useAppContext } from '../../contexts/AppContext';
import LogService from '../../services/log.service';
import UserService from '../../services/user.service';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  inviteButton: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  root: {
    padding: theme.spacing(3),
  },
  subtitle1: {
    fontSize: 16,
    paddingTop: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  titleContainer: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
  },
  userTable: {
    paddingRight: theme.spacing(6),
  },
}));

const defaultCriteria = {
  limit: 10,
  orderField: null,
  orderType: 'ASC',
  page: 0,
  roles: [ROLES.ADMIN.role, ROLES.LEAD_TECH.role, ROLES.RADIOLOGIST.role, ROLES.TECHNOLOGIST.role],
};

const UsersView = () => {
  const classes = useStyles();
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [updateType, setUpdateType] = useState(null);
  const [statusAction, setStatusAction] = useState(null); // 'enable' or 'disable'
  const [users, setUsers] = useState({ data: [], total: 0 });
  const [searchCriteria, setSearchCriteria] = useState(defaultCriteria);
  const { enqueueSnackbar } = useSnackbar();
  const userService = useMemo(() => new UserService(), []);
  const logService = useMemo(() => new LogService(), []);
  const { currentUser } = useAppContext();

  const fetchUsers = useCallback(async () => {
    try {
      const fetchedUsers = await userService.getUsers(searchCriteria);

      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      enqueueSnackbar('Failed to fetch users', { variant: 'error' });
    }
  }, [searchCriteria, userService, enqueueSnackbar]);

  const updateSearchCriteria = useCallback(
    async (criteria) => {
      const updatedCriteria = {
        ...searchCriteria,
        ...criteria,
      };
      setSearchCriteria(updatedCriteria);

      logService.create({
        action: ACTIONS.USERS_VIEWED,
        details: {
          Amount: updatedCriteria?.limit || 10,
          Page: updatedCriteria?.page + 1,
        },
      });
    },
    [searchCriteria, logService]
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    logService.create({
      action: ACTIONS.USERS_VIEWED,
      details: {
        Amount: 10,
        Page: 1,
      },
    });
  }, [logService]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenResendModal = (user) => {
    setSelectedUser(user);
    setIsResendModalOpen(true);
  };

  const handleCloseResendModal = () => {
    setIsResendModalOpen(false);
    setSelectedUser(null);
  };

  const handleOpenStatusModal = (user, action) => {
    setSelectedUser(user);
    setStatusAction(action);
    setIsStatusModalOpen(true);
  };

  const handleCloseStatusModal = () => {
    setIsStatusModalOpen(false);
    setSelectedUser(null);
    setStatusAction(null);
  };

  const handleOpenUpdateModal = (user, type) => {
    setSelectedUser(user);
    setUpdateType(type);
    setIsUpdateModalOpen(true);
  };

  const handleCloseUpdateModal = () => {
    setIsUpdateModalOpen(false);
  };

  const handleUpdateModalExited = () => {
    setSelectedUser(null);
    setUpdateType(null);
  };

  const handleCriteriaChange = useCallback(
    (criteria) => {
      const updatedCriteria = {
        ...defaultCriteria,
        ...criteria,
      };

      updateSearchCriteria(updatedCriteria);
    },
    [updateSearchCriteria]
  );

  return (
    <Container component="main" className={classes.container}>
      <div className={classes.header}>
        <div className={classes.titleContainer}>
          <Typography variant="h4" align="left" className="page-header">
            Users
          </Typography>
          <Typography variant="body1" align="left" className={classes.subtitle1}>
            View a list of radiologists and technicians in your workspace
          </Typography>
        </div>

        {[ROLES.ADMIN.role, ROLES.LEAD_TECH.role].includes(currentUser?.role) && (
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleOpenModal} className={classes.inviteButton}>
              Invite User
            </Button>
            <Button variant="outlined" color="primary" onClick={() => setIsImportModalOpen(true)} className={classes.inviteButton}>
              Import Users
            </Button>
          </div>
        )}
      </div>

      <Grid container className={classes.container}>
        <Grid item xs={12}>
          <UsersTable
            onCriteriaChange={handleCriteriaChange}
            users={users.data?.map((user) => ({
              ...user,
              departmentName: user.department?.name,
              facilityName: user.facility?.name,
            }))}
            total={users.total}
            onResendInvitation={handleOpenResendModal}
            onChangeUserStatus={handleOpenStatusModal}
            onUpdateUser={handleOpenUpdateModal}
          />

          <InviteUserModal open={isModalOpen} onClose={handleCloseModal} onUserInvited={fetchUsers} />

          <ResendInvitationModal open={isResendModalOpen} onClose={handleCloseResendModal} user={selectedUser} onResendSuccess={fetchUsers} />

          <UpdateUserModal
            open={isUpdateModalOpen}
            onClose={handleCloseUpdateModal}
            onExited={handleUpdateModalExited}
            user={selectedUser}
            updateType={updateType}
            onUserUpdated={fetchUsers}
          />

          <UserStatusModal open={isStatusModalOpen} onClose={handleCloseStatusModal} user={selectedUser} action={statusAction} onStatusChanged={fetchUsers} />

          <ImportUsersModal open={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} onUsersImported={fetchUsers} />
        </Grid>
      </Grid>
    </Container>
  );
};
export default UsersView;
